import axios from "axios";
import store from "../store";
import { getCookie } from "../../utils/cookie/Cookies";

const baseURL = process.env.REACT_APP_API_URL;

export const publicInstance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
    accept: "*/*",
  },
});

export const authInstance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
    accept: "*/*",
  },
});

// 토큰 재발급 중복 방지
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 토큰 재발급 함수 (인터셉터에서 공통 사용)
const refreshAccessToken = async () => {
  const refreshToken = getCookie("refreshToken");
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await publicInstance.post("/auth/reissue", {
      refreshToken: refreshToken,
    });
    const newAccessToken = response.data.accessToken;

    store.dispatch({
      type: "REFRESH_TOKEN_SUCCESS",
      isAuthenticated: true,
      payload: newAccessToken,
    });

    return newAccessToken;
  } catch (error) {
    store.dispatch({
      type: "REFRESH_TOKEN_FAILURE",
      isAuthenticated: false,
      payload: error,
    });
    return null;
  }
};

authInstance.interceptors.request.use(
  async (config) => {
    let token = store.getState().auth.accessToken;

    // 토큰이 없고, refreshToken이 있으면 재발급 시도
    if (!token) {
      const refreshToken = getCookie("refreshToken");
      if (refreshToken) {
        // 이미 재발급 중이면 완료될 때까지 대기
        if (isRefreshing && refreshPromise) {
          token = await refreshPromise;
        } else {
          // 새로 재발급 시작
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
          token = await refreshPromise;
          isRefreshing = false;
          refreshPromise = null;
        }
      }
    }

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 401 에러 시 토큰 재발급 후 재시도
authInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 에러이고, 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 토큰 재발급 중이면 큐에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return authInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();

        isRefreshing = false;

        if (!newAccessToken) {
          processQueue(error, null);
          return Promise.reject(error);
        }

        processQueue(null, newAccessToken);

        // 원래 요청 재시도
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return authInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
